import React from "react";
import { Mail, Send, MessageSquare, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

export default function ContactPage() {
  const [form, setForm] = React.useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = React.useState({});
  const [sent, setSent] = React.useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Votre nom est requis";
    if (!form.email.trim()) e.email = "Votre email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email invalide";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Message trop court (min. 10 caractères)";
    return e;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Merci de corriger les champs du formulaire.");
      return;
    }
    const subject = encodeURIComponent(`Contact BoostPro — ${form.name}`);
    const body = encodeURIComponent(`Nom : ${form.name}\nEmail : ${form.email}\n\n${form.message}`);
    window.location.href = `mailto:boostpro.app@gmail.com?subject=${subject}&body=${body}`;
    setSent(true);
    toast.success("Votre client mail va s'ouvrir.");
  };

  const onChange = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  return (
    <main data-testid="page-contact" className="relative pt-32 md:pt-40 pb-24">
      <div className="absolute inset-0 bg-grid opacity-25 [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_70%)]" />
      <div className="absolute -top-10 left-1/4 h-[360px] w-[360px] rounded-full bg-blue-600/20 blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 h-[300px] w-[300px] rounded-full bg-emerald-500/15 blur-[120px]" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-blue-400">Contact</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            Parlons de votre <span className="text-gradient-blue">activité.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-400">
            Une question, une suggestion, un partenariat ? Nous lisons chaque message.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-5 gap-10">
          {/* Left info */}
          <aside className="lg:col-span-2 space-y-5">
            <a
              href="mailto:boostpro.app@gmail.com"
              data-testid="contact-info-email"
              className="block rounded-2xl border border-white/5 bg-slate-900/60 hover:bg-slate-900/80 p-6 transition-colors group"
            >
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-blue-500/15 flex items-center justify-center">
                  <Mail size={20} className="text-blue-300" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold">Email</div>
                  <div className="mt-1 text-white font-medium group-hover:text-blue-200 transition-colors">
                    boostpro.app@gmail.com
                  </div>
                  <p className="mt-1 text-sm text-slate-400">Réponse sous 24-48h ouvrées.</p>
                </div>
              </div>
            </a>

            <div className="rounded-2xl border border-white/5 bg-slate-900/60 p-6">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <MessageSquare size={20} className="text-emerald-300" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold">Support</div>
                  <p className="mt-1 text-slate-300">Bug, suggestion ou question fonctionnelle — écrivez-nous, nous prenons le temps de comprendre.</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-900/60 p-6">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-blue-500/15 flex items-center justify-center">
                  <Clock size={20} className="text-blue-300" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold">Disponibilité</div>
                  <p className="mt-1 text-slate-300">Du lundi au vendredi, 9h-18h (heure de Paris).</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-900/60 p-6">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <MapPin size={20} className="text-emerald-300" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold">Origine</div>
                  <p className="mt-1 text-slate-300">Une équipe française, à l'écoute des indépendants.</p>
                </div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="lg:col-span-3 rounded-3xl border border-white/10 bg-slate-900/70 backdrop-blur-xl p-8 md:p-10"
            noValidate
          >
            <h2 className="font-display text-2xl font-semibold text-white">Écrivez-nous</h2>
            <p className="mt-1 text-slate-400 text-sm">Le formulaire ouvrira votre application mail avec le message pré-rempli.</p>

            <div className="mt-7 space-y-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Nom</label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Votre nom"
                  className={`w-full rounded-xl bg-slate-950/70 border ${errors.name ? "border-red-500/60" : "border-white/10"} focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition-all`}
                />
                {errors.name && <p data-testid="contact-error-name" className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Email</label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="vous@exemple.fr"
                  className={`w-full rounded-xl bg-slate-950/70 border ${errors.email ? "border-red-500/60" : "border-white/10"} focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition-all`}
                />
                {errors.email && <p data-testid="contact-error-email" className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Message</label>
                <textarea
                  data-testid="contact-input-message"
                  rows={6}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Comment pouvons-nous vous aider ?"
                  className={`w-full rounded-xl bg-slate-950/70 border ${errors.message ? "border-red-500/60" : "border-white/10"} focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition-all resize-none`}
                />
                {errors.message && <p data-testid="contact-error-message" className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
              </div>

              <button
                data-testid="contact-submit-btn"
                type="submit"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-medium px-7 py-3.5 glow-blue transition-all"
              >
                Envoyer le message
                <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
              </button>

              {sent && (
                <p data-testid="contact-sent-msg" className="text-sm text-emerald-300">
                  Votre application mail a été ouverte avec le message pré-rempli.
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
